import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { reduxForm } from 'redux-form';
import { wrapper } from 'ggtmo-utils';
import { mutateTree } from '@atlaskit/tree';
import { toast } from 'react-toastify';

// Components
import {
  Container, Row, Col, Card, CardBody,
} from 'reactstrap';
import SectionEditorForm from './EditorForm';
import AsyncComponent from '../../../shared/components/AsyncComponent';
import EditorControls from '../../../shared/components/EditorControls';
import DeleteConfirmModal from '../../../shared/components/DeleteConfirmModal';

// Redux
import {
  toggleDeleteConfirmModal,
} from '../../../redux/actions/editorActions';
import {
  // Async
  sectionsGetSection,
  sectionsCreateSection,
  sectionsUpdateSection,
  sectionsUpdateAndPublishSection,
  sectionsPublishSection,
  sectionsUnpublishSection,
  sectionsDeleteSection,
  sectionsGetPage,

  // Sync
  sectionsUpdateKey,
  sectionsReinitializeSectionState,
} from '../redux/actions';
import {
  SECTIONS__GET_SECTION,
  SECTIONS__CREATE_SECTION,
  SECTIONS__UPDATE_SECTION,
  SECTIONS__UPDATE_AND_PUBLISH_SECTION,
  SECTIONS__PUBLISH_SECTION,
  SECTIONS__UNPUBLISH_SECTION,
  SECTIONS__DELETE_SECTION,
  SECTIONS__GET_PAGE,
} from '../redux/actionCreators';

// Classes
import BaseEditor from '../../../shared/BaseEditor';

// Utils
import getUserType from '../../../shared/utils/getUserType';

class Editor extends BaseEditor {
  static propTypes = {
    // Editor
    deleteConfirmModalOpen: PropTypes.bool.isRequired,

    // Sections
    sectionsActions: PropTypes.instanceOf(Object).isRequired,
    sections: PropTypes.instanceOf(Object).isRequired,
    pages: PropTypes.instanceOf(Object).isRequired,

    // Actions
    getSection: PropTypes.func.isRequired,
    toggleDocumentsModal: PropTypes.func.isRequired,

    // Form
    editorForm: PropTypes.instanceOf(Object),
    pristine: PropTypes.bool.isRequired,
    change: PropTypes.func.isRequired,
  };

  static defaultProps = {
    // Form
    editorForm: {},
  };

  appName = 'sections';

  redirectName = 'content';

  modelName = 'section';

  actionCreators = {
    getSection: SECTIONS__GET_SECTION,
    get: SECTIONS__GET_PAGE,
    create: SECTIONS__CREATE_SECTION,
    update: SECTIONS__UPDATE_SECTION,
    updateAndPublish: SECTIONS__UPDATE_AND_PUBLISH_SECTION,
    delete: SECTIONS__DELETE_SECTION,
    publish: SECTIONS__PUBLISH_SECTION,
    unpublish: SECTIONS__UNPUBLISH_SECTION,
  };

  toastMessages = {
    create: {
      SUCCESS: 'Created section.',
      ERROR: 'Error creating section.',
    },
    update: {
      SUCCESS: 'Saved section.',
      ERROR: 'Error saving section.',
    },
    updateAndPublish: {
      SUCCESS: 'Saved and published section. Your changes are visible to the public.',
      ERROR: 'Error saving section.',
    },
    delete: {
      SUCCESS: 'Deleted section,',
      ERROR: 'Error deleting section.',
    },
    publish: {
      SUCCESS: 'Published section. Your changes are visible to the public.',
      ERROR: 'Error publishing section.',
    },
    unpublish: {
      SUCCESS: 'Unpublished section. It is no longer visible to the public.',
      ERROR: 'Error unpublishing section.',
    },
    submit: {
      SUCCESS: 'Submitted for review. You will be notified if it is approved for publish.',
      ERROR: 'Error submitting for review.',
    },
  };

  extraRequestParameters = {
    create: {
      isChild: true,
    },
    update: {
      isChild: true,
    },
  };

  componentDidMount() {
    const { get, match: { params: { slug } } } = this.props;
    get({
      list: false,
      tree: true,
      slug,
    });
  }

  handleOnEdit = (event) => {
    const { id } = event.currentTarget.dataset;
    const {
      getSection: get, change, initialize, updateKey: updateKeyAction,
    } = this.props;
    get({
      id,
      list: false,
    })
      .then((action) => {
        if (action.type === SECTIONS__GET_SECTION.SUCCESS) {
          change('child', action.response.instance);
          initialize(this._getFormValues());
          updateKeyAction({
            key: action.response.instance.id,
          });
        }
      });
  };

  _changeTree = (value) => {
    const { change } = this.props;
    change('tree', value);
  };

  _initialize = (action) => {
    const { initialize } = this.props;
    const { instance, instanceTreeItem, tree } = action.response;
    const formValues = this._getFormValues();
    initialize({
      ...formValues,
      child: {
        ...formValues.child,
        ...instance,
      },
      tree: tree || mutateTree(formValues.tree, instance.id, instanceTreeItem),
    });
  };

  handleOnUpdate = () => {
    const { update, pages } = this.props;
    const formValues = this._getFormValues();
    return update({
      ...formValues,
      ...this.extraRequestParameters.update,
      pageSlug: pages.instance.slug,
      updateImageSet: !!formValues.child.imageSet,
      updateDocumentSet: !!formValues.child.documentSet,
    })
      .then((action) => {
        this._onUpdatePromiseCallback(action, this.actionCreators.update, this.toastMessages.update);
        if (action.type === this.actionCreators.update.SUCCESS) {
          this._initialize(action);
        }
      });
  };

  handleOnUpdateAndPublish = () => {
    const { updateAndPublish, pages } = this.props;
    const formValues = this._getFormValues();
    return updateAndPublish({
      ...this._getFormValues(),
      ...this.extraRequestParameters.updateAndPublish,
      pageSlug: pages.instance.slug,
      updateImageSet: !!formValues.child.imageSet,
      updateDocumentSet: !!formValues.child.documentSet,
    })
      .then((action) => {
        this._onUpdatePromiseCallback(action, this.actionCreators.updateAndPublish,
          this.toastMessages.updateAndPublish);
        if (action.type === this.actionCreators.updateAndPublish.SUCCESS) {
          this._initialize(action);
        }
      });
  };

  handleOnPublish = () => {
    const { publish, pages } = this.props;
    const formValues = this._getFormValues();
    return publish({
      pageSlug: pages.instance.slug,
      child: {
        id: formValues.child.id,
      },
    })
      .then((action) => {
        this._onUpdatePromiseCallback(action, this.actionCreators.publish, this.toastMessages.publish);
        if (action.type === this.actionCreators.publish.SUCCESS) {
          this._initialize(action);
        }
      });
  };

  handleOnUnpublish = () => {
    const { unpublish, pages } = this.props;
    const formValues = this._getFormValues();
    return unpublish({
      pageSlug: pages.instance.slug,
      child: {
        id: formValues.child.id,
      },
    })
      .then((action) => {
        this._onUpdatePromiseCallback(action, this.actionCreators.unpublish, this.toastMessages.unpublish);
        if (action.type === this.actionCreators.unpublish.SUCCESS) {
          this._initialize(action);
        }
      });
  };

  handleOnDelete = () => {
    const {
      delete: delete_, toggleDeleteConfirmModal: toggle, pages, clearEditor: clear,
    } = this.props;
    return delete_({
      pageSlug: pages.instance.slug,
      id: this._getFormValues().child.id,
    })
      .then((action) => {
        if (action.type === this.actionCreators.delete.SUCCESS) {
          this._changeTree(action.response.tree);
          toggle();
          clear();
          toast(this.toastMessages.delete.SUCCESS, { type: toast.TYPE.SUCCESS });
        }
        if (action.type === this.actionCreators.delete.ERROR) {
          toast(this._parseErrorResponse(action.error, this.toastMessages.delete.ERROR),
            { type: toast.TYPE.ERROR, autoClose: 5000 });
        }
      });
  };

  _handleOnSubmit = (event) => {
    // This is just a placeholder to
    // stop the prop-type warnings.
    event.preventDefault();
  };

  render() {
    // Props
    const {
      // Sections
      sectionsActions: {
        SECTIONS__GET_PAGE: getPageAction,
        SECTIONS__GET_SECTION: getSectionAction,
        SECTIONS__UPDATE_SECTION: updateSectionAction,
        SECTIONS__UPDATE_AND_PUBLISH_SECTION: updateAndPublishSectionAction,
        SECTIONS__PUBLISH_SECTION: publishSectionAction,
        SECTIONS__UNPUBLISH_SECTION: unpublishSectionAction,
        SECTIONS__DELETE_SECTION: deleteSectionAction,
      },
      sections,

      // Redux
      pages,
      deleteConfirmModalOpen,

      // Form
      pristine,
      editorForm,
    } = this.props;

    return (
      <>
        <Container>
          <Row>
            <Col xs={9}>
              <AsyncComponent action={getPageAction}>
                <SectionEditorForm
                  initialValues={{ tree: pages.instance.tree, child: sections.instance }}
                  enableReinitialize

                  // Actions
                  getSectionAction={getSectionAction}

                  // Callbacks
                  onEdit={this.handleOnEdit}
                  onSubmit={this._handleOnSubmit}
                />
              </AsyncComponent>
            </Col>
            {editorForm.values && editorForm.values.child && (
              <>
                <Col style={{ height: '100%' }}>
                  <Card>
                    <CardBody className="shadow">
                      <EditorControls
                        userType={getUserType()}
                        instanceType="section"
                        isNew={false}
                        pristine={pristine}
                        instance={sections.instance}

                        // Actions#
                        updateAction={updateSectionAction}
                        updateAndPublishAction={updateAndPublishSectionAction}
                        publishAction={publishSectionAction}
                        unpublishAction={unpublishSectionAction}
                        deleteAction={deleteSectionAction}

                        // Callbacks
                        onUpdate={this.handleOnUpdate}
                        onUpdateAndPublish={this.handleOnUpdateAndPublish}
                        onPublish={this.handleOnPublish}
                        onUnpublish={this.handleOnUnpublish}
                        onDelete={this.handleOnToggleDeleteConfirmModal}
                      />
                    </CardBody>
                  </Card>
                </Col>
              </>
            )}
          </Row>
        </Container>
        <DeleteConfirmModal
          open={deleteConfirmModalOpen}
          instanceType="section"
          action={deleteSectionAction}

          // Callbacks
          toggle={this.handleOnToggleDeleteConfirmModal}
          deleteAnyway={this.handleOnDelete}
        />
      </>
    );
  }
}

const mapStateToProps = (state) => {
  const {
    deleteConfirmModalOpen,
  } = state.editor;

  const {
    actions: sectionsActions,
    pages,
    sections,
  } = state.sections;

  const {
    editorForm,
  } = state.form;

  return {
    // Editor
    deleteConfirmModalOpen,

    // Sections
    sectionsActions,
    pages,
    sections,

    // Form
    editorForm,
  };
};

const mapDispatchToProps = {
  // Async
  get: sectionsGetPage,
  getSection: sectionsGetSection,
  create: sectionsCreateSection,
  update: sectionsUpdateSection,
  updateAndPublish: sectionsUpdateAndPublishSection,
  delete: sectionsDeleteSection,
  publish: sectionsPublishSection,
  unpublish: sectionsUnpublishSection,

  // Sync
  reinitialize: sectionsReinitializeSectionState,
  updateKey: sectionsUpdateKey,
  toggleDeleteConfirmModal,
};

const withForm = {
  form: 'editorForm',
};

export default wrapper({
  component: Editor,
  wrappers: [
    connect(mapStateToProps, mapDispatchToProps),
    withRouter,
    reduxForm(withForm),
  ],
});
